import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { distinctDiaryLabelsQueryKey } from "./selectDistinctDiaryLabels";
import { diaryLabelsQueryKey } from "./selectDiaryLabels";

export interface InsertDiaryLabelParams {
    label_name: string;
    label_description?: string | null;
    label_url?: string | null;
    label_variant?: string | null;
}

export async function insertDiaryLabel({
    label_name,
    label_description,
    label_url,
    label_variant,
}: InsertDiaryLabelParams): Promise<{ label_id: string; label_name: string }> {
    const { data, error } = await supabaseClient
        .from("diary_labels")
        .insert({
            label_name: label_name.trim(),
            label_description: label_description ?? null,
            label_url: label_url ?? null,
            label_variant: label_variant ?? null,
        })
        .select("label_id, label_name")
        .single();

    if (error) throw error;
    return data;
}

export function useInsertDiaryLabel() {
    const queryClient = useQueryClient();
    const toastCopy = useToastCopy();

    return useMutation({
        mutationFn: insertDiaryLabel,
        onError: (error: Error) => {
            toast.error(error.message);
        },
        onSuccess: () => {
            toast.success(toastCopy.labelCreated);
            void queryClient.invalidateQueries({ queryKey: distinctDiaryLabelsQueryKey });
            void queryClient.invalidateQueries({ queryKey: diaryLabelsQueryKey });
            void queryClient.invalidateQueries({ queryKey: ["diary-label-options"] });
        },
    });
}
